import { useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import Dexie from 'dexie';
import db from '@/db/db';
import { DecoratedClient, decorateClient, Client } from '@/db/types/client';
import { Document } from '@/db/types/document';
import { isPendingDocument } from '@/db/services/document_service';

export interface OrganizationClientsBase {
  organizationClients: Client[] | undefined;
  clientIdSet: Set<number>;
  clientIdsKey: string;
  clientsForTable: DecoratedClient[] | undefined;
  unsyncedDocs: Document[] | undefined;
}

export function useOrganizationClients(organizationId: number | null): OrganizationClientsBase {
  const organizationClients = useLiveQuery(
    async () => {
      if (!organizationId) return undefined;
      return db.clients
        .where('[organization_id+updated_at]')
        .between([organizationId, Dexie.minKey], [organizationId, Dexie.maxKey])
        .reverse()
        .and(c => c.id !== 0)
        .limit(100)
        .toArray();
    },
    [organizationId]
  );

  const clientIdSet = useMemo(() => {
    if (!organizationClients) return new Set<number>();
    return new Set(organizationClients.map(c => c.id));
  }, [organizationClients]);

  const clientIdsKey = useMemo(() => {
    if (!organizationClients) return '';
    return organizationClients.map(c => c.id).sort((a, b) => a - b).join(',');
  }, [organizationClients]);

  const clientsForTable = useMemo(() => {
    if (!organizationClients) return undefined;
    return organizationClients.map(c => decorateClient(c));
  }, [organizationClients]);

  const unsyncedDocs = useLiveQuery(
    async () => {
      if (!organizationClients) return undefined;
      if (clientIdSet.size === 0) return [];
      return db.documents
        .where('client_id')
        .anyOf([...clientIdSet])
        .and(isPendingDocument)
        .toArray();
    },
    [clientIdsKey]
  );

  return { organizationClients, clientIdSet, clientIdsKey, clientsForTable, unsyncedDocs };
}
